import { VoucherCopyDialogStore } from './voucher-copy-dialog.store';
import { fb } from '../../../../../shared/shared.global-service';
import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { copyVoucherFrag, getVoucherFrag } from './voucher-copy-dialog.query';
import { VoucherGridItemObject } from '@it2go/types';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormatVoucherPipe } from '../../../../../shared/pipes/format-voucher.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { FieldErrorFactory } from '../../../../../shared/service/field-error.service';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';
import { VoucherDetailService } from '../../voucher-detail-page/voucher-detail-page.service';
import { contentGlobalSuccess } from '../../../../content.error';
import { VoucherCopyInput } from '@libs/types/src/services/input-type/voucher/voucher.copy-input';
import { VoucherDetailPageStore } from '../../voucher-detail-page/voucher-detail-page.store';
import { VoucherDetailPageStore2 } from '../../voucher-detail-page/voucher-detail-page.store2';
import {
    ErrorFloatMessageComponent,
} from '../../../../../../components/kit/float-message/error-float-message.component';

export function datesValidator(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
        const fromDate = form.get('dateFrom');
        const fromDateVal = fromDate?.value;
        const toDate = form.get('dateTo');
        const toDateVal = toDate?.value;

        if (!fromDateVal) {
            fromDate?.setErrors(FieldErrorFactory.requiredError());
        }

        if (!toDateVal) {
            toDate?.setErrors(FieldErrorFactory.requiredError());
        }

        const from = new Date(fromDateVal);
        const to = new Date(toDateVal);

        if (from >= to) {
            toDate?.setErrors(FieldErrorFactory.invalidValidityPeriodError());
        } else {
            const errors = toDate?.errors;
            if (errors) {
                delete errors.invalidValidityPeriodError;
                toDate.setErrors(Object.keys(errors).length ? errors : null);
            }
        }

        return null;
    };
}

@Component({
    selector: 'app-voucher-copy-dialog',
    templateUrl: './voucher-copy-dialog.component.html',
    styleUrl: './voucher-copy-dialog.component.scss',
    providers: [
        VoucherCopyDialogStore,
        VoucherDetailService,
        VoucherDetailPageStore,
        VoucherDetailPageStore2,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        FormatVoucherPipe,
        TranslatePipe,
        ActionButtonsComponent,
        ErrorFloatMessageComponent,
    ],
})
export class VoucherCopyDialogComponent extends BaseDialogComponent {

    public readonly store = inject(VoucherCopyDialogStore);
    public readonly detailService = inject(VoucherDetailService);

    protected readonly prefix = 'voucher.copyDialog.';

    protected form = fb.group(
        {
            dateFrom: fb.control<Date | null>(new Date()),
            dateTo: fb.control<Date | null>(null),
        },
        { validators: datesValidator() },
    );

    protected voucherId: number = this.dialogConfig.data.voucherId;

    protected existsVoucher: boolean = false;
    protected existingVoucher: VoucherGridItemObject | null = null;

    private voucher: VoucherGridItemObject | null = null;

    private formChanged = toSignal(this.form.valueChanges);

    constructor() {
        super();

        this.getVoucher();

        effect(async () => {
            const value = this.formChanged();

            if (this.voucher && value && value.dateFrom && value.dateTo) {
                // check if exist some vouchers for current date range
                const vouchers = await this.store.api.getVouchers({
                    paging: {
                        page: 1,
                        itemsPerPage: 1,
                    },
                    filter: [
                        [
                            {
                                column: 'doctorId',
                                value: [this.voucher.doctorId!],
                                operator: 'EQ',
                            },
                        ],
                        [
                            {
                                column: 'folderId',
                                value: [String(this.voucher.folderId || 0)],
                                operator: 'EQ',
                            },
                        ],
                        [
                            {
                                column: 'issueDate',
                                value: [value?.dateTo?.toISOString()],
                                operator: 'LTE',
                            },
                        ],
                        [
                            {
                                column: 'voucherTo',
                                value: [value?.dateFrom?.toISOString()],
                                operator: 'GTE',
                            },
                        ],
                    ],
                    sorter: [
                        {
                            column: 'voucherTo',
                            direction: 'ASC',
                        },
                    ],
                });

                this.existingVoucher = vouchers?.['items']?.[0] || null;
                this.existsVoucher = this.existingVoucher !== null;
            }
        });
    }

    public override async save(_event: Event) {
        if (isInvalid(this.form) || this.existsVoucher) return;

        // calculate new sequence number due to connection to previous ended voucher, ...
        const lastVoucher = await this.detailService.getLastVoucher(
            this.voucher?.doctorId || '',
            this.form.value.dateFrom!,
        );
        const nextVoucher = await this.detailService.getNextVoucher(
            this.voucher?.doctorId || '',
            this.form.value.dateFrom!,
        );

        const seq = this.detailService.getVoucherSequenceInfo(
            this.form.value.dateFrom!,
            this.form.value.dateTo!,
            lastVoucher,
            nextVoucher,
        );

        await this.store.api.copyVoucher(<VoucherCopyInput>{
            voucherId: this.voucherId,
            voucherPrevId: seq.lastVoucherId,
            sequenceNumber: seq.sequenceNumber || 1,
            dateFrom: this.form.value.dateFrom!.toISOString(),
            dateTo: this.form.value.dateTo!.toISOString(),
        }, copyVoucherFrag);
        contentGlobalSuccess.next('common.copied');
        this.dialogRef.close(true);
    }

    private async getVoucher() {
        const vouchers = await this.store.api.getVouchers(FilterBuilder.filter({ id: this.voucherId }), getVoucherFrag);
        this.voucher = vouchers?.['items']?.[0] || null;

        if (this.voucher) {
            let dateFrom = new Date(this.voucher.closureDate || this.voucher.validTo);
            let dateTo: Date;
            const now = new Date();

            if (now > new Date(this.voucher.issueDate) && now < dateFrom) {
                dateFrom.setDate(dateFrom.getDate() + 1);
                dateTo = new Date(dateFrom);
                dateTo.setMonth(dateFrom.getMonth() + 1, 0);
            } else {
                dateFrom = now;
                dateTo = new Date(dateFrom);
                dateTo.setMonth(dateFrom.getMonth() + 1, 0);
            }

            this.form.patchValue({
                dateFrom,
                dateTo,
            });
        }
    }
}
